import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit, Print } from '@mui/icons-material'

import {
  useDELETE_attendanceMutation,
  useGET_attendanceByIdQuery,
  usePATCH_attendanceMutation,
  usePOST_attendanceMutation,
} from 'redux/api/attendanceApi'
import { useGET_groupCourseSessionSelectQuery } from 'redux/api/sessionsApi'
import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/attendanceModel'
import { attendanceStatusSelectData, crudColorMap, crudIconMap, printDiv } from 'utils/helpers'

import { ActionType, IAttendanceData } from 'types'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
  report?: boolean
}

const AttendanceForm: FC<IProps> = ({ action, id, viewOnly, report = false }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = report ? '/attendance-reports' : '/attendance'
  const editSlug = `/${report ? 'attendance-reports' : 'attendance'}/edit/${id}` 

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.attendance')}`,
    entityId: id,
    getEntityQuery: useGET_attendanceByIdQuery,
    createEntityMutation: usePOST_attendanceMutation,
    editEntityMutation: usePATCH_attendanceMutation,
    deleteEntityMutation: useDELETE_attendanceMutation,
  })

  const { student, status, session, absence_minutes, participation_points, comment } = formModel()

  const serverValues = entity?.id
    ? {
        ...entity,
        status: { id: entity.status, title: entity.status },
        absence_minutes: Number(entity.absence_minutes),
        participation_points: Number(entity.participation_points),
      }
    : null
  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        const data: IAttendanceData = {
          student_id: values.student.id,
          status: values.status?.id,
          session_id: values.session?.id,
          absence_minutes: Number(values.absence_minutes),
          participation_points: Number(values.participation_points),
          comment: values.comment,
        }
        handleSubmit(action, id, data)
      }}
    >
      {({ values, setFieldValue, errors }) => (
        <Form>
          <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
            <Grid item xs={12} md={3}>
              <SelectField
                fieldProps={{ ...student }}
                apiCall={useGET_studentsProfileSelectQuery}
                disabled={viewOnly}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SelectField
                fieldProps={{ ...session }}
                filters={{ student: values.student?.id }}
                apiCall={useGET_groupCourseSessionSelectQuery}
                disabled={viewOnly}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SelectField
                fieldProps={{ ...status }}
                apiCall={attendanceStatusSelectData}
                disabled={viewOnly}
              />
            </Grid>

            {(values.status?.id === 'tardy' || values.status?.id === 'tardy_excused') &&
            <Grid item xs={12} md={3}>
              <InputField 
                fieldProps={{ ...absence_minutes }} 
                disabled={viewOnly} 
                />
            </Grid>}
            <Grid item xs={12}>
              <RichTextField
                fieldProps={{ ...comment }}
                disabled={viewOnly}
                label={`${t('common.comment')}`}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }} className="transparent-background">
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>

                {action === 'view' && report && 
                  <Button
                  variant='outlined'
                  color='black'
                  onClick={ () => { printDiv('template') } }
                  sx={{ minHeight: '2.5rem', width: '10rem', marginRight: '10px' }}
                >
                  <Print style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.print')}`}
                </Button>
               }

                {action === 'view' && !report && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
               }

              
                {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    sx={{ width: '10rem' }}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Grid>
          </Grid>

          <div id="template" style={{ display: 'none' }}>
            <table className="border-table"><tbody>  
              <tr><th style={{ width: '40%' }}><b>{ student.label }:</b></th><td>{ entity?.student?.title }</td></tr>
              <tr><th><b>{ status.label }:</b></th><td>{ entity?.status }</td></tr>
              <tr><th><b>{ session.label }:</b></th><td>{ entity?.session?.title }</td></tr>
              <tr><th><b>{ absence_minutes.label }:</b></th><td>{ entity?.absence_minutes }</td></tr>
              <tr><th><b>{ participation_points.label }:</b></th><td>{ entity?.participation_points }</td></tr>
              <tr><th><b>{ comment.label }:</b></th><td>{ entity?.comment }</td></tr>
            </tbody></table>    
          </div>

        </Form>
      )}
    </Formik>
  )
}
export default AttendanceForm
